/*
  MetaLeadAdsSettings — Owner/Manager settings card for the
  Meta Instant Form → Zapier → Enquiries Inbox integration
  (Meta Lead Ads Spec §14 admin monitoring panel).

  Renders:
    - endpoint URL + copy button
    - last received / last successful / last failure timestamps
    - 30-day counters (received / imported / duplicate / failed)
    - a ready-to-paste Zapier "Custom Request" configuration
    - active/inactive traffic-light status
  Never displays or requests the X-Inbox-Token itself — that is
  the WordPress inbox token (shared per spec §3) and stays in
  the WordPress Inbox card, which already handles rotation.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Zap, Copy, RefreshCcw, CheckCircle2, AlertTriangle, Info,
} from "lucide-react";

function CopyLine({ value, testid }) {
  return (
    <div className="flex items-center gap-1.5">
      <code
        className="flex-1 min-w-0 px-2 py-1 bg-zinc-100 rounded text-2xs font-mono text-zinc-800 break-all"
        data-testid={testid}
      >
        {value}
      </code>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2"
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success("Copied");
        }}
        data-testid={`${testid}-copy`}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch (_) {
    return iso;
  }
}

export default function MetaLeadAdsSettings() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/settings/enquiries/inbox/meta-lead/status");
      setStatus(r.data || null);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await load();
      toast.success("Status refreshed");
    } finally {
      setRefreshing(false);
    }
  };

  const endpointUrl = `${
    (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")
  }/api/enquiries/inbox/meta-lead`;

  // Status pill state — mirrors the WordPress inbox convention.
  //  * active    — a lead has landed in the last 30 days
  //  * quiet     — endpoint is up but no leads received yet
  //  * failing   — the most recent event was a failure
  let stateBadge = { label: "Quiet", tone: "outline",
    className: "border-zinc-300 text-zinc-600 bg-zinc-50" };
  if (status?.last_failure_at && (!status?.last_success_at
        || status.last_failure_at > status.last_success_at)) {
    stateBadge = { label: "Failing", tone: "outline",
      className: "border-red-300 text-red-800 bg-red-50" };
  } else if (status?.active) {
    stateBadge = { label: "Active", tone: "default",
      className: "bg-emerald-700 text-white" };
  }

  return (
    <Card
      className="p-5 rounded-lg border border-zinc-200 shadow-none space-y-5"
      data-testid="meta-lead-ads-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-orange-100 flex items-center justify-center">
              <Zap className="h-5 w-5 text-orange-700" />
            </div>
            <div>
              <div className="text-sm font-semibold text-zinc-950">
                Meta Lead Ads (via Zapier)
              </div>
              <div className="text-xs text-zinc-500">
                Facebook &amp; Instagram Instant Form leads → Enquiries Inbox
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant={stateBadge.tone}
            className={stateBadge.className}
            data-testid="meta-lead-status-badge"
          >
            {stateBadge.label}
          </Badge>
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            disabled={refreshing || loading}
            data-testid="meta-lead-refresh-button"
          >
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-semibold">Could not load status</div>
            <div>{String(error)}</div>
          </div>
        </div>
      )}

      {/* Endpoint URL */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">
          Zapier webhook URL
        </div>
        <CopyLine value={endpointUrl} testid="meta-lead-endpoint" />
        <div className="mt-1 text-2xs text-zinc-500">
          In Zapier, use <em>Webhooks by Zapier → Custom Request</em>{" "}
          with method <code className="font-mono">POST</code>, content-type{" "}
          <code className="font-mono">application/json</code>, and add the{" "}
          <code className="font-mono">X-Inbox-Token</code> header (same secret{" "}
          as your WordPress inbox).
        </div>
        <div
          className="mt-2 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-2xs text-blue-900"
          data-testid="meta-lead-token-help"
        >
          <div className="font-semibold text-blue-900 mb-0.5">Where do I get the token?</div>
          Open{" "}
          <a
            href="/enquiries/inbox"
            className="underline decoration-blue-500 underline-offset-2 hover:text-blue-950"
            data-testid="meta-lead-token-link"
          >
            Enquiries → Inbox
          </a>
          {" "}→ scroll to the <em>Inbox token &amp; webhook</em> panel →{" "}
          <strong>Generate token</strong> (or <strong>Rotate token</strong> if
          one already exists). The plaintext token is shown{" "}
          <strong>only once</strong> in the amber banner right after you click —
          copy it there and paste it into Zapier's{" "}
          <code className="font-mono">X-Inbox-Token</code> header. Rotating is
          safe: both your WordPress webhook and Zapier just need the new value.
        </div>
      </div>

      {/* Counters */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">
          Delivery status (last 30 days)
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div
            className="rounded-md border border-zinc-200 bg-white px-3 py-2"
            data-testid="meta-lead-counter-received"
          >
            <div className="text-2xs text-zinc-500">Received</div>
            <div className="text-lg font-semibold text-zinc-950">
              {status?.received_30d ?? "—"}
            </div>
          </div>
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50/40 px-3 py-2"
            data-testid="meta-lead-counter-imported"
          >
            <div className="text-2xs text-emerald-800">Imported</div>
            <div className="text-lg font-semibold text-emerald-900">
              {status?.imported_30d ?? "—"}
            </div>
          </div>
          <div
            className="rounded-md border border-amber-200 bg-amber-50/40 px-3 py-2"
            data-testid="meta-lead-counter-duplicate"
          >
            <div className="text-2xs text-amber-800">Duplicate (idempotent)</div>
            <div className="text-lg font-semibold text-amber-900">
              {status?.duplicate_30d ?? "—"}
            </div>
          </div>
          <div
            className="rounded-md border border-red-200 bg-red-50/40 px-3 py-2"
            data-testid="meta-lead-counter-failed"
          >
            <div className="text-2xs text-red-800">Failed</div>
            <div className="text-lg font-semibold text-red-900">
              {status?.failed_30d ?? "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Timestamps */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div>
          <div className="uppercase tracking-wider text-zinc-500 font-semibold mb-1">
            Last received
          </div>
          <div className="text-zinc-800" data-testid="meta-lead-last-received">
            {fmtDateTime(status?.last_received_at)}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-zinc-500 font-semibold mb-1">
            Last successful import
          </div>
          <div className="text-zinc-800 flex items-center gap-1.5" data-testid="meta-lead-last-success">
            {status?.last_success_at && (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" />
            )}
            {fmtDateTime(status?.last_success_at)}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-wider text-zinc-500 font-semibold mb-1">
            Last failure
          </div>
          <div className="text-zinc-800" data-testid="meta-lead-last-failure">
            {fmtDateTime(status?.last_failure_at)}
          </div>
          {status?.last_failure_reason && (
            <div
              className="mt-1 text-2xs text-red-800 bg-red-50/60 border border-red-200 rounded px-2 py-1 break-words"
              data-testid="meta-lead-last-failure-reason"
              title={status.last_failure_reason}
            >
              {String(status.last_failure_reason).slice(0, 200)}
            </div>
          )}
        </div>
      </div>

      {/* Zapier hint */}
      <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700 flex items-start gap-2">
        <Info className="h-4 w-4 mt-0.5 text-zinc-500 shrink-0" />
        <div className="space-y-1">
          <div>
            <span className="font-semibold">Payload shape:</span>{" "}
            <code className="font-mono">source</code>,{" "}
            <code className="font-mono">platform</code>,{" "}
            <code className="font-mono">meta_lead_id</code>,{" "}
            <code className="font-mono">contact{"{"}first_name, last_name, email, phone{"}"}</code>,{" "}
            <code className="font-mono">marketing{"{"}form_id, form_name, campaign_id, ...{"}"}</code>,{" "}
            <code className="font-mono">answers{"{"}...{"}"}</code>.
          </div>
          <div>
            Duplicate deliveries of the same <code className="font-mono">meta_lead_id</code>{" "}
            are idempotent — they return HTTP 200 with{" "}
            <code className="font-mono">status: "duplicate"</code> and the prior{" "}
            enquiry id. Existing contacts stay linked but new enquiries are still created —{" "}
            a client can legitimately enquire more than once.
          </div>
        </div>
      </div>
    </Card>
  );
}
